import { useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import {
  Button,
  Drawer,
  RoundButton,
  Spinner,
  Typography,
} from '@lamimed/pantheon';
import Image from 'next/legacy/image';

import { Product } from 'models/Product';

import { useGetProductBySlug } from 'hooks/useGetProductBySlug';
import { useSchedule } from 'hooks/useSchedule';
import { useScheduleStep } from 'hooks/useScheduleStep';

import { calculateTotalPrice } from 'templates/Schedule/shared/utils/calculateTotalPrice';
import { scheduleSteps } from 'templates/Schedule/shared/utils/scheduleSteps';

import { useCart } from './useCart';

import * as S from './styles';

interface CartDrawerProps {
  productSlug: string;
}

export const CartDrawer: React.FC<CartDrawerProps> = ({ productSlug }) => {
  const { steps, fillProductInfo } = useSchedule();

  const { goToScheduleStep } = useScheduleStep();

  const { isCartOpen, setIsCartOpen } = useCart();
  const [quantity, setQuantity] = useState(1);

  const { data } = useGetProductBySlug(productSlug);

  const productRoute = `/${data?.categories[0].slug}/${data?.slug}`;

  const handleConfirmButton = (productData: Product) => {
    fillProductInfo(productData.activityId, productSlug, quantity);

    const { index, path } = scheduleSteps.pickDate;

    goToScheduleStep({
      step: {
        index,
        path: `${path}?productId=${productData.activityId}&productSlug=${productSlug}&quantity=${quantity}`,
      },
      steps,
      previousRoute: {
        path: productRoute,
        index: 0,
      },
    });

    datadogRum.addAction('bookProductsFromCart', {
      cart: {
        product: productSlug,
        type: productData?.attendenceType,
        quantity: quantity,
      },
    });
  };

  return (
    <Drawer
      dismissible
      open={isCartOpen}
      onClose={() => setIsCartOpen(false)}
      closeButtonColor="dark"
    >
      {data ? (
        <>
          <S.ProductCard>
            <S.Wrapper>
              <S.ThumbBox>
                <Image
                  src={`/images/illustration/simplified/${data?.categories[0].slug}.png`}
                  width={33}
                  height={27}
                  quality={100}
                />
              </S.ThumbBox>

              <S.ProductInfo>
                <Typography marginBottom="small">
                  {data?.activityTitle}
                </Typography>

                <S.SmallText>{data?.categories[0].title}</S.SmallText>
              </S.ProductInfo>
            </S.Wrapper>

            <S.QuantityWrapper>
              {quantity > 1 ? (
                <Button
                  icon="minuscircle"
                  variant="clear"
                  color="secondary"
                  onClick={() => setQuantity((current) => current - 1)}
                  aria-label="sottrarre prodotto"
                />
              ) : (
                <Button
                  icon="delete"
                  variant="clear"
                  color="state__error"
                  onClick={() => setIsCartOpen(false)}
                  aria-label="rimuovere il prodotto"
                />
              )}

              <S.QuantityDisplay data-testid="product-quantity-display">
                {quantity}
              </S.QuantityDisplay>

              <Button
                icon="pluscircle"
                variant="clear"
                color="secondary"
                onClick={() => {
                  if (quantity < data.limit) {
                    setQuantity((current) => current + 1);
                  }
                }}
                aria-label="aggiungi prodotto"
                disabled={quantity === data.limit}
              />
            </S.QuantityWrapper>
          </S.ProductCard>

          <Typography
            textAlign="center"
            marginBottom="medium"
            marginTop="medium"
          >
            A partire da {calculateTotalPrice(data.price, quantity)}
          </Typography>

          <S.ButtonWrapper>
            <RoundButton onClick={() => handleConfirmButton(data)} expanded>
              Conferma
            </RoundButton>
          </S.ButtonWrapper>
        </>
      ) : (
        <Spinner size="medium" />
      )}
    </Drawer>
  );
};
